import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Switch } from '@/components/inspector-fields/Switch/Switch';
import { SecondaryButton } from '@/components/buttons/SecondaryButton/SecondaryButton';
import DropShadowMenu, {
  type DropShadowSettings,
} from '@/components/inspector-fields/DropShadowMenu/DropShadowMenu';
import { computeFloatingPosition } from '@/utils/computeFloatingPosition';

export type ShadowValue = 'none' | 'drop';

interface ShadowFieldProps {
  value: ShadowValue;
  settings: DropShadowSettings;
  onChange: (value: ShadowValue) => void;
  onSettingsChange: (next: DropShadowSettings) => void;
  className?: string;
}

const defaultSettings: DropShadowSettings = {
  x: 0,
  y: 0,
  blur: 0,
  spread: 0,
  color: '#000000',
};

export const ShadowField: React.FC<ShadowFieldProps> = ({
  value,
  settings,
  onChange,
  onSettingsChange,
  className = '',
}) => {
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const isEnabled = value === 'drop';

  const handleToggle = (checked: boolean) => {
    const next: ShadowValue = checked ? 'drop' : 'none';
    onChange(next);
    if (!checked) {
      setPos(null);
    } else if (!settings) {
      onSettingsChange({ ...defaultSettings });
    }
  };

  return (
    <div ref={anchorRef} className={className}>
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <Switch label="Shadow" checked={isEnabled} onCheckedChange={handleToggle} />
        </div>
        {isEnabled && (
          <SecondaryButton
            size="sm"
            text="Edit"
            icon={null}
            onClick={() => {
              if (pos) {
                setPos(null);
                return;
              }
              const inspectorEl = anchorRef.current?.closest('.inspector') as HTMLElement | null;
              const p = inspectorEl
                ? computeFloatingPosition({
                    inspectorEl,
                    anchorEl: anchorRef.current,
                    width: 240,
                    height: 360,
                    preferSide: 'left',
                  })
                : null;
              if (p) setPos({ top: p.top, left: p.left });
            }}
          />
        )}
      </div>
      {isEnabled &&
        pos &&
        createPortal(
          <DropShadowMenu
            outside
            floatingPosition={pos}
            settings={settings || defaultSettings}
            onChange={onSettingsChange}
            onClose={() => setPos(null)}
          />,
          document.body,
        )}
    </div>
  );
};

export default ShadowField;
